import { EventEmitter } from 'events'

const loadAsyncScript = (appId: string, done: () => void): void => {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.async = true
  script.src = `https://widget.intercom.io/widget/${appId}`
  script.onload = done

  const firstScript = document.getElementsByTagName('script')[0]
  firstScript.parentNode?.insertBefore(script, firstScript)
}

export default class Intercom extends EventEmitter {
  appId: string
  defaultOptions: { app_id: string }
  ready: boolean
  isBooted: boolean

  unreadCount: number

  private _visible: boolean = false
  get visible(): boolean {
    return this._visible
  }

  set visible(value: boolean) {
    this.emit(value ? 'show' : 'hide')
    this._visible = value
  }

  constructor(appId: string) {
    super()

    this.appId = appId
    this.defaultOptions = { app_id: appId }
    this.ready = false
    this.isBooted = false
    this.visible = false
    this.unreadCount = 0

    this.load()
  }

  // Load intercom script with defer
  load(): void {
    if (!window || !document) return

    const load = (): void => loadAsyncScript(this.appId, () => this.init())

    if (document.readyState === 'complete') {
      loadAsyncScript(this.appId, () => this.init())
    } else {
      window.addEventListener('load', load, false)
    }
  }

  // Init Intercom service
  init(): void {
    this.ready = true
    this.callIntercom('onHide', () => (this.visible = false))
    this.callIntercom('onShow', () => (this.visible = true))
    this.callIntercom(
      'onUnreadCountChange',
      (unreadCount: number) => (this.unreadCount = unreadCount),
    )

    this.emit('ready')
  }

  // Boot Intercom service with user
  boot(options: Record<string, unknown>): void {
    this.callIntercom('boot', Object.assign({}, this.defaultOptions, options))
    this.isBooted = true
  }

  callIntercom(...args: unknown[]): void {
    if (!window.Intercom) return
    return (window.Intercom as unknown as (...args: unknown[]) => void)(...args)
  }

  shutdown(): void {
    this.isBooted = false
    this.callIntercom('shutdown')
  }

  update(...options: unknown[]): void {
    this.callIntercom('update', ...options)
  }

  show(): void {
    this.callIntercom('show')
    this.emit('show')
  }

  hide(): void {
    this.callIntercom('hide')
    this.emit('hide')
  }

  showMessages(): void {
    this.callIntercom('showMessages')
  }

  showNewMessage(content: unknown): void {
    this.callIntercom('showNewMessage', ...[content])
  }

  trackEvent(name: string, ...metadata: unknown[]): void {
    this.callIntercom('trackEvent', ...[name, ...metadata])
  }

  startTour(id: string, ...metadata: unknown[]): void {
    this.callIntercom('startTour', ...[id, ...metadata])
  }

  getVisitorId(): void {
    this.callIntercom('getVisitorId')
  }
}
